import type { DriverTarget } from "@skaile/workspaces/plugin-registry";
import type { Logger } from "@skaile/workspaces/types";
import * as z from "zod";
import { getBridgeLogger } from "../logger.js";
import { fetchProviderModels, type ModelEntry } from "../models.js";
import { BUILTIN_DRIVER_CATALOG } from "../registry.js";
import type { AgentConfig, DriverInfo } from "../types.js";
import { GeminiFamilyDriver } from "./gemini.js";

/**
 * Agent driver for **Qwen Code** (`qwen`) — a fork of Gemini CLI.
 *
 * Reuses {@link GeminiFamilyDriver}'s stream-json event mapping. Unlike Gemini,
 * Qwen is **model-agnostic / OpenAI-compatible**: it reads `OPENAI_API_KEY`,
 * `OPENAI_BASE_URL`, and `OPENAI_MODEL`, so it works against any
 * OpenAI-completions endpoint (Alibaba DashScope by default).
 *
 * @remarks
 * - Spawns `qwen -p "<prompt>" -o stream-json --include-partial-messages`.
 * - Sets `QWEN_CODE_UNATTENDED_RETRY=1` so the CLI backs off on 429/529 in
 *   unattended driver contexts.
 * - Supports headless session resume via `--resume <id>`.
 *
 * @see https://github.com/QwenLM/qwen-code
 * @docLink packages/bridge/drivers#qwen-driver
 */
export class QwenDriver extends GeminiFamilyDriver {
  readonly driverInfo: DriverInfo = {
    id: "qwen",
    name: "Qwen Code",
    modelAgnostic: true,
    supportsInBandAbort: false,
  };

  protected readonly log: Logger = getBridgeLogger("qwen");
  protected readonly defaultBin = "qwen";
  protected readonly productName = "Qwen Code";
  protected readonly envPrefix = "QWEN";

  protected buildArgs(message: string, resumeId: string | undefined): string[] {
    const args = ["-p", message, "-o", "stream-json", "--include-partial-messages"];
    if (this.config.model) args.push("-m", this.config.model);
    if (resumeId) args.push("--resume", resumeId);
    return args;
  }

  protected buildEnv(): Record<string, string> {
    const env = this.baseEnv(["OPENAI_API_KEY", "OPENAI_BASE_URL", "OPENAI_MODEL"]);
    const key =
      this.config.apiKeys?.openai ??
      this.config.apiKeys?.qwen ??
      this.config.env?.OPENAI_API_KEY ??
      process.env.OPENAI_API_KEY;
    if (key) env.OPENAI_API_KEY = key;
    if (this.config.model) env.OPENAI_MODEL = this.config.model;
    env.QWEN_CODE_UNATTENDED_RETRY = "1";
    return env;
  }

  protected installHint(): string {
    return "Qwen Code is not installed or not on your PATH. Install it with `npm i -g @qwen-code/qwen-code`.";
  }

  override async listModels(): Promise<ModelEntry[]> {
    const key = this.config.apiKeys?.openai ?? process.env.OPENAI_API_KEY;
    if (!key) return [];
    const result = await fetchProviderModels("openai", key);
    return result.ok ? result.models : [];
  }
}

/**
 * `DriverTarget` wrapper for the qwen driver. Registered into the plugin
 * registry by `registerBuiltinDrivers()`.
 */
export const qwenDriverTarget: DriverTarget<AgentConfig> = {
  id: "qwen",
  displayName: BUILTIN_DRIVER_CATALOG.qwen?.name ?? "Qwen Code",
  apiVersion: 1,
  configSchema: z.custom<AgentConfig>(() => true),
  create: async (config, _ctx) => new QwenDriver(config),
  listModels: async (apiKeys) =>
    new QwenDriver({ cwd: process.cwd(), sessionId: "model-list", apiKeys }).listModels(),
};
